import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { useAcquisitionGetProductTariffs } from '@eneco/dc/hooks/sales';
import { getMarketingProductCodes, getProductTariffsProducts, getTariffValues } from '@eneco/dc/modelContract/tariffs';
import useDC from '@eneco/dc/useDC';
import { useFormatter } from '@eneco/i18n';
import { NotificationBox, Skeleton, Stack, Stretch, Table as SparkyTable, Text } from '@eneco/sparky';

import type { ModelContractTableContent } from './model-contract-table-content.types';

/** Fixed costs (e.g. `{vasteLeveringskostenGasPerMaandVast}`) are monthly prices, everything else is a per-unit tariff. */
const isFixedCostPlaceholder = (placeholder: string) => placeholder.replace(/^{/, '').startsWith('vaste');

/**
 * `ModelContractTable` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/ModelContractTable/ModelContractTable.tsx`: a table
 * whose first column is author copy and whose remaining cells are placeholder tokens swapped for live
 * tariffs from the Digital Core. Unblocked by the `lib:i18n` widening — every number in it is a
 * formatted euro amount, and `currency.euro` vs `currency.euroFiveDecimals` is chosen per cell.
 *
 * NOTE (schema gap): the bundle models this block as `{ title }` only — the rows were never modelled,
 * the same gap `Table` has. Rows are still read from `content`, because the dataset is schemaless and
 * may yet carry what the schema omits; until then a block renders its title plus a dev-only notice, so
 * an empty table reads as "not migrated" rather than as a broken component. This is the one place the
 * two lanes visibly differ: the tariff lookup itself is Digital Core data and works identically on
 * both, but with no rows there are no cells to put it in.
 */
const ModelContractTable: FC = () => {
  const content = useContent<ModelContractTableContent>();
  const { label } = useDC();
  const { currency } = useFormatter();

  const { headerRows, rows } = content;

  const codes = getMarketingProductCodes(label);
  // Only body cells look tariffs up, so the request is worth making only when there are rows. `null`
  // is the hook's "do not fetch", and a block that never fetches is never loading — without this a
  // title-only block (all the schema supplies today) posts every tariff product to the Digital Core
  // and shows a 480px skeleton first, for an answer nothing on screen can use.
  const wantsTariffs = Boolean(rows?.length);
  const { data, isLoading } = useAcquisitionGetProductTariffs(
    codes && wantsTariffs ? { products: getProductTariffsProducts(codes) } : null,
  );

  const tariffs = getTariffValues(codes, data?.productTariffs);

  if (isLoading) {
    return (
      <Stack direction="row" gap="2">
        <Stretch>
          <Skeleton height="480px" />
        </Stretch>
      </Stack>
    );
  }

  if (!headerRows?.length && !rows?.length) {
    return (
      <>
        {content.title && <Text weight="bold">{content.title}</Text>}
        {process.env.NODE_ENV !== 'production' && (
          <NotificationBox
            isAlert={false}
            title="Model contract table rows not migrated"
            text="The schema models this block as a title only."
          />
        )}
      </>
    );
  }

  return (
    <SparkyTable title={content.title}>
      {headerRows?.[0] && (
        <SparkyTable.Header>
          {(headerRows[0].columns ?? []).map((column, index) => (
            <HeaderCell key={index} content={column.content} />
          ))}
        </SparkyTable.Header>
      )}

      {rows?.map((row, rowIndex) => (
        <SparkyTable.Row key={rowIndex}>
          {(row.columns ?? []).map((column, columnIndex) => (
            <BodyCell
              key={columnIndex}
              content={column.content}
              // The first column is author copy; every other cell is a tariff placeholder.
              isCopy={columnIndex === 0}
              tariffs={tariffs}
              currency={currency}
            />
          ))}
        </SparkyTable.Row>
      ))}
    </SparkyTable>
  );
};

const HeaderCell: FC<{ content: unknown }> = ({ content }) => {
  const { adapter } = useCms();
  return <Text>{adapter.toRichText(content) ?? ''}</Text>;
};

type CurrencyFormatter = ReturnType<typeof useFormatter>['currency'];

const BodyCell: FC<{
  content: unknown;
  isCopy: boolean;
  tariffs: Record<string, number | undefined>;
  currency: CurrencyFormatter;
}> = ({ content, isCopy, tariffs, currency }) => {
  const { adapter } = useCms();
  const html = adapter.toRichText(content) ?? '';

  if (isCopy) return <RichText html={html} />;

  const tariff = tariffs[html];
  const formatTariff = isFixedCostPlaceholder(html) ? currency.euro : currency.euroFiveDecimals;

  return <RichText html={tariff === undefined ? '--' : formatTariff(tariff)} />;
};

export default ModelContractTable;
